import { useState, useEffect } from 'react';
import { Card, Descriptions, Button, message, Modal, Form, Input, Space } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { updateProfileName } from '@/api/developer';
import { useAuthStore } from '@/stores/authStore';
import type { Developer } from '@/types/developer';
import { 
  SaveOutlined, UserOutlined, 
  MailOutlined, MobileOutlined, IdcardOutlined, 
  CalendarOutlined, ClockCircleOutlined, WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

export const Profile = () => {
  const [isEditNameModalVisible, setIsEditNameModalVisible] = useState(false);
  const { currentDeveloper, setCurrentDeveloper } = useAuthStore();
  const navigate = useNavigate();

  const updateNameMutation = useMutation({
    mutationFn: (name: string) => updateProfileName(name),
    onSuccess: (_, newName) => {
      message.success('用户名更新成功');
      
      if (currentDeveloper) {
        const updatedDeveloper: Developer = {
          ...currentDeveloper,
          name: newName,
          updated_at: new Date().toISOString(),
        };
        setCurrentDeveloper(updatedDeveloper);
      }
      
      setIsEditNameModalVisible(false);
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '用户名更新失败';
      message.error(errorMessage);
    },
  });

  useEffect(() => {
    if (!currentDeveloper) {
      window.location.href = '/login';
    }
  }, [currentDeveloper]);

  if (!currentDeveloper) {
    return <LoadingSpinner />;
  }

  // ✅ 判断是否显示邮箱/手机号（只读显示）
  const hasEmail = !!currentDeveloper.email && currentDeveloper.email.trim() !== '';
  const hasPhone = !!currentDeveloper.phone && currentDeveloper.phone.trim() !== '';

  // 统一样式
  const itemStyle = { paddingBottom: '16px' } as React.CSSProperties;
  const labelStyle = { 
    width: '120px', 
    fontWeight: 600, 
    color: '#667085',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as React.CSSProperties;
  const contentStyle = { color: '#1D2129', fontSize: '14px' } as React.CSSProperties;

  // ✅ 处理注销账号点击
  const handleDeleteAccount = () => {
    Modal.confirm({
      title: '⚠️ 确认注销账号',
      icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div className="space-y-2">
          <p>注销后以下数据将<strong>永久删除</strong>且无法恢复：</p>
          <ul className="list-disc list-inside text-sm text-gray-600">
            <li>开发者账号及所有个人信息</li>
            <li>所有创建的 App 及密钥</li>
            <li>所有产品及配置数据</li>
            <li>所有上传的面板资源</li>
            <li>所有上传的图片文件</li>
          </ul>
          <p className="text-red-500 font-medium mt-2">此操作不可撤销！</p>
        </div>
      ),
      okText: '前往注销页面',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => {
        navigate('/settings/delete-account');
      },
    });
  };

  return (
    <div className="w-full space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <UserOutlined className="text-blue-500 text-xl" />
          个人信息管理
        </h1>
        <p className="text-gray-500 mt-1">查看并管理你的开发者个人资料。</p>
      </div>
      
      <Card 
        title="基本信息" 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{
          '--ant-card-head-font-size': '18px',
          '--ant-card-head-font-weight': 600,
          '--ant-card-head-color': '#1D2129',
          '--ant-card-padding': '24px'
        } as React.CSSProperties}
      >
        <Descriptions 
          column={1} 
          bordered={false}
          size="middle"
          colon={false}
        >
          {/* 用户名 - 可编辑 */}
          <Descriptions.Item 
            label={<><UserOutlined /> 用户名</>}
            style={itemStyle}
            labelStyle={labelStyle}
            contentStyle={contentStyle}
          >
            <div className="flex items-center justify-between w-full">
              <span className="text-gray-900">
                {currentDeveloper.name || <span className="text-gray-400">未设置</span>}
              </span>
              <Button 
                type="link" 
                size="small" 
                onClick={() => setIsEditNameModalVisible(true)}
                disabled={updateNameMutation.isPending}
                className="text-blue-500 hover:text-blue-600 p-0 h-auto -mr-2"
              >
                修改用户名
              </Button>
            </div>
          </Descriptions.Item>
      
          {/* 邮箱 - 只读显示 + 注销链接 */}
          {hasEmail && (
            <Descriptions.Item 
              label={<><MailOutlined /> 邮箱</>}
              style={itemStyle}
              labelStyle={labelStyle}
              contentStyle={contentStyle}
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-gray-900">{currentDeveloper.email}</span>
                <Button 
                  type="link" 
                  size="small" 
                  danger
                  onClick={handleDeleteAccount}
                  // icon={<LogoutOutlined />}
                  className="text-red-500 hover:text-red-600 p-0 h-auto -mr-2 flex items-center gap-1"
                >
                  注销账号
                </Button>
              </div>
            </Descriptions.Item>
          )}
          
          {/* 手机号 - 只读显示 + 注销链接 */}
          {hasPhone && (
            <Descriptions.Item 
              label={<><MobileOutlined /> 手机号</>}
              style={itemStyle}
              labelStyle={labelStyle}
              contentStyle={contentStyle}
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-gray-900">{currentDeveloper.phone}</span>
                <Button 
                  type="link" 
                  size="small" 
                  danger
                  onClick={handleDeleteAccount}
                  // icon={<LogoutOutlined />}
                  className="text-red-500 hover:text-red-600 p-0 h-auto -mr-2 flex items-center gap-1"
                >
                  注销账号
                </Button>
              </div>
            </Descriptions.Item>
          )}
          
          {/* 如果都没有联系方式，显示注销入口 */}
          {!hasEmail && !hasPhone && (
            <Descriptions.Item 
              label={<><MailOutlined /> 联系方式</>}
              style={itemStyle}
              labelStyle={labelStyle}
              contentStyle={contentStyle}
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-gray-400">未设置邮箱或手机号</span>
                <Button 
                  type="link" 
                  size="small" 
                  danger
                  onClick={handleDeleteAccount}
                  // icon={<LogoutOutlined />}
                  className="text-red-500 hover:text-red-600 p-0 h-auto -mr-2 flex items-center gap-1"
                >
                  注销账号
                </Button>
              </div>
            </Descriptions.Item>
          )}
          
          {/* 开发者 ID - 只读 */}
          <Descriptions.Item 
            label={<><IdcardOutlined /> 开发者 ID</>}
            style={itemStyle}
            labelStyle={labelStyle}
            contentStyle={contentStyle}
          >
            {currentDeveloper.id}
          </Descriptions.Item>
          
          {/* 注册时间 - 只读 */}
          <Descriptions.Item 
            label={<><CalendarOutlined /> 注册时间</>}
            style={itemStyle}
            labelStyle={labelStyle}
            contentStyle={contentStyle}
          >
            {new Date(currentDeveloper.created_at).toLocaleString('zh-CN')}
          </Descriptions.Item>
          
          {/* 最后更新 - 只读 */}
          <Descriptions.Item 
            label={<><ClockCircleOutlined /> 最后更新</>}
            style={itemStyle}
            labelStyle={labelStyle}
            contentStyle={contentStyle}
          >
            {new Date(currentDeveloper.updated_at).toLocaleString('zh-CN')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* ✅ 只保留修改用户名的 Modal */}
      <Modal
        title="修改用户名"
        open={isEditNameModalVisible}
        onCancel={() => setIsEditNameModalVisible(false)}
        footer={null}
        destroyOnClose
        width={400}
        centered
        className="profile-name-edit-modal"
        style={{
          '--ant-modal-header-font-size': '18px',
          '--ant-modal-header-font-weight': 600,
          '--ant-modal-border-radius': '8px'
        } as React.CSSProperties}
      >
        <Form
          layout="vertical"
          onFinish={(values: { name: string }) => updateNameMutation.mutateAsync(values.name)}
          initialValues={{ name: currentDeveloper?.name }}
          className="mt-4"
          requiredMark={false}
        >
          <Form.Item
            name="name"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 2, message: '用户名至少 2 个字符' },
              { max: 50, message: '用户名最多 50 个字符' }
            ]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ marginBottom: 16 }}
          >
            <Input 
              placeholder="请输入新的用户名" 
              prefix={<UserOutlined className="text-gray-400" />}
              allowClear
              className="rounded-lg"
              size="middle"
              disabled={updateNameMutation.isPending}
            />
          </Form.Item>

          <Form.Item
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            className="mb-0"
          >
            <Space size="middle" className="flex justify-end w-full">
              <Button 
                onClick={() => setIsEditNameModalVisible(false)}
                disabled={updateNameMutation.isPending}
                className="hover:bg-gray-50 transition-colors"
                size="middle"
              >
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={updateNameMutation.isPending}
                icon={<SaveOutlined />}
                className="hover:shadow-md transition-shadow px-6"
                size="middle"
              >
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};